// Runtime for templates generated by scripts/compile-dc.mjs.
// Mirrors Claude Design's dc-runtime (support.js) so screens render and
// behave exactly like the design prototypes.
import React from 'react';

export const h = React.createElement;
export const F = React.Fragment;

// ---------- expressions: {{ a.b[c] }}, !x, a === 'b', literals ----------
const IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

function parensWrapWhole(expr: string) {
  let depth = 0;
  for (let i = 0; i < expr.length - 1; i++) {
    if (expr[i] === '(') depth++;
    else if (expr[i] === ')') {
      depth--;
      if (depth === 0) return false;
    }
  }
  return true;
}

function findTopLevelEquality(expr: string) {
  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    const c = expr[i];
    if (c === '[' || c === '(') depth++;
    else if (c === ']' || c === ')') depth--;
    else if (depth === 0 && (c === '=' || c === '!') && expr[i + 1] === '=') {
      if (i > 0 && (expr[i - 1] === '=' || expr[i - 1] === '!')) continue;
      if (!expr.slice(0, i).trim()) continue;
      const op = expr[i + 2] === '=' ? c + '==' : c + '=';
      return { index: i, op };
    }
  }
  return null;
}

function resolvePath(vals: any, expr: string): any {
  const head = expr.match(IDENT_RE);
  if (!head) return undefined;
  let cur = vals == null ? undefined : vals[head[0]];
  let i = head[0].length;
  while (i < expr.length) {
    if (expr[i] === '.') {
      const m = expr.slice(i + 1).match(IDENT_RE) || expr.slice(i + 1).match(/^\d+/);
      if (!m) return undefined;
      cur = cur == null ? undefined : cur[m[0]];
      i += 1 + m[0].length;
    } else if (expr[i] === '[') {
      let depth = 1;
      let j = i + 1;
      while (j < expr.length && depth > 0) {
        if (expr[j] === '[') depth++;
        else if (expr[j] === ']') {
          depth--;
          if (depth === 0) break;
        }
        j++;
      }
      if (depth !== 0) return undefined;
      const key = resolve(vals, expr.slice(i + 1, j));
      cur = cur == null ? undefined : cur[key];
      i = j + 1;
    } else {
      return undefined;
    }
  }
  return cur;
}

export function resolve(vals: any, src: string): any {
  const expr = String(src).trim();
  if (!expr) return undefined;
  if (expr[0] === '(' && expr[expr.length - 1] === ')' && parensWrapWhole(expr)) {
    return resolve(vals, expr.slice(1, -1));
  }
  const eq = findTopLevelEquality(expr);
  if (eq) {
    const lv = resolve(vals, expr.slice(0, eq.index));
    const rv = resolve(vals, expr.slice(eq.index + eq.op.length));
    switch (eq.op) {
      case '===': return lv === rv;
      case '!==': return lv !== rv;
      // eslint-disable-next-line eqeqeq
      case '==': return lv == rv;
      // eslint-disable-next-line eqeqeq
      default: return lv != rv;
    }
  }
  if (expr[0] === '!') return !resolve(vals, expr.slice(1));
  if (expr === 'true') return true;
  if (expr === 'false') return false;
  if (expr === 'null') return null;
  if (expr === 'undefined') return undefined;
  if (NUMBER_RE.test(expr)) return Number(expr);
  if (expr.length >= 2 && (expr[0] === '"' || expr[0] === "'") && expr[expr.length - 1] === expr[0]) {
    return expr.slice(1, -1);
  }
  return resolvePath(vals, expr);
}

// ---------- helpers used by generated templates ----------
export const R = resolve;

/** Attribute mixing literal text and {{ }} parts (odd indices are expressions). */
export function C(vals: any, parts: string[]) {
  return parts.map((s, i) => (i & 1 ? resolve(vals, s) ?? '' : s)).join('');
}

const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

/** Inline style from a CSS string (or pass-through object). */
export function S(style: any): React.CSSProperties | undefined {
  if (style == null) return undefined;
  if (typeof style !== 'string') return style;
  const o: Record<string, string> = {};
  for (const decl of style.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    o[prop.startsWith('--') ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
  }
  return o as React.CSSProperties;
}

/** {{ expr }} inside text — rendered in a span.sc-interp exactly like the design runtime. */
export function I(vals: any, expr: string, key: number) {
  const v = resolve(vals, expr);
  if (v === undefined || v === null || typeof v === 'boolean') return null;
  if (React.isValidElement(v) || Array.isArray(v)) return h(F, { key }, v);
  return h('span', { key, className: 'sc-interp' }, String(v));
}

export const L = (list: any): any[] => (Array.isArray(list) ? list : []);
export const VAL = (v: any) => (v === undefined ? '' : v);

// ---------- logic base class ----------
// Same contract as the design runtime: setState applies synchronously to
// `this.state` (code may read it right after), then re-renders the host.
export class DCLogic<P = any, St = any> {
  props: P;
  state: St = {} as St;
  __host?: DCHost;
  constructor(props?: P) {
    this.props = (props || {}) as P;
  }
  setState(update: Partial<St> | ((s: St) => Partial<St>), cb?: () => void) {
    this.__host?.__setLogicState(update, cb);
  }
  forceUpdate() {
    this.__host?.forceUpdate();
  }
  componentDidMount() {}
  componentDidUpdate(_prevProps: P) {}
  componentWillUnmount() {}
  renderVals(): Record<string, any> {
    return {};
  }
}

type LogicCtor = new (props: any) => DCLogic;

interface HostProps {
  logic: LogicCtor;
  template: (vals: any) => React.ReactNode;
  css: string;
  props?: Record<string, any>;
}

export class DCHost extends React.Component<HostProps, { v: number }> {
  logic: DCLogic;
  state = { v: 0 };
  constructor(p: HostProps) {
    super(p);
    this.logic = new p.logic(p.props || {});
    this.logic.__host = this;
  }
  __setLogicState(update: any, cb?: () => void) {
    const prev = this.logic.state as any;
    const patch = typeof update === 'function' ? update(prev) : update;
    this.logic.state = { ...prev, ...patch };
    this.setState((s) => ({ v: s.v + 1 }), cb);
  }
  componentDidMount() {
    this.logic.componentDidMount();
  }
  componentDidUpdate(prev: HostProps) {
    this.logic.props = this.props.props || {};
    this.logic.componentDidUpdate(prev.props || {});
  }
  componentWillUnmount() {
    this.logic.componentWillUnmount();
  }
  render() {
    const userProps = this.props.props || {};
    this.logic.props = userProps;
    const vals = { ...userProps, ...(this.logic.renderVals() || {}) };
    return h('div', { className: 'sc-host' }, h('style', null, this.props.css), this.props.template(vals));
  }
}
